/**
 * Executive Summary Component
 * Displays portfolio health score, total value, and key metrics
 * Supports both global (all portfolios) and client-specific modes
 */

import React from 'react';
import { ArrowUp, ArrowDown, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import HealthScoreGauge from './HealthScoreGauge';
import PortfolioValue from './atoms/PortfolioValue';
import MetricCard from './atoms/MetricCard';
import { PortfolioOverview } from '../../services/api/portfolioApiService';

interface ExecutiveSummaryProps {
  mode: 'global' | 'client';
  data: PortfolioOverview | null;
  globalSummary?: {
    totalAUM: number;
    averageHealth: number;
    needsAttention: number;
    needsRebalancing: number;
    totalClients: number;
  };
  loading?: boolean;
  hideHealthScore?: boolean; // Option to hide health score gauge
}

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({
  mode,
  data,
  globalSummary,
  loading,
  hideHealthScore = false
}) => {
  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // TASK-281: Calculate daily change from API data (no random fallback)
  // Daily change should come from portfolio history/performance data
  const calculateDailyChange = () => {
    if (!data) return { value: 0, percentage: 0 };
    // Use actual daily_change from API if available, otherwise show 0
    const dataAny = data as any;
    const changePercent = dataAny.daily_change_percent || 0;
    const changeValue = dataAny.daily_change_value || (data.total_value * changePercent) / 100;
    return { value: changeValue, percentage: changePercent };
  };

  const dailyChange = data ? calculateDailyChange() : { value: 0, percentage: 0 };

  // Determine traffic light status
  const getTrafficLightStatus = (score: number) => {
    if (score >= 80) return { color: 'bg-green-500', label: 'Healthy' };
    if (score >= 60) return { color: 'bg-yellow-500', label: 'Attention' };
    return { color: 'bg-red-500', label: 'Critical' };
  };

  const healthScore = mode === 'global' 
    ? Math.round(globalSummary?.averageHealth || 0)
    : (data?.health_score || 0);
    
  const trafficLight = getTrafficLightStatus(healthScore);

  // Determine key drivers based on metrics
  const getKeyDrivers = () => {
    if (mode === 'global' && globalSummary) {
      return [
        {
          label: 'Portfolios Need Attention',
          value: globalSummary.needsAttention,
          status: globalSummary.needsAttention > 0 ? 'warning' : 'good',
          icon: globalSummary.needsAttention > 0 ? ArrowUp : ArrowDown
        },
        {
          label: 'Require Rebalancing',
          value: globalSummary.needsRebalancing,
          status: globalSummary.needsRebalancing > 0 ? 'warning' : 'good',
          icon: globalSummary.needsRebalancing > 0 ? ArrowUp : ArrowDown
        },
        {
          label: 'Total Clients',
          value: globalSummary.totalClients,
          status: 'neutral',
          icon: AlertCircle
        }
      ];
    }

    if (data) {
      const liquidityRatio = (data.total_cash / data.total_value) * 100;
      const plPercent = (data.total_unrealized_pl / data.total_securities) * 100;
      
      return [
        {
          label: 'Liquidity',
          value: `${liquidityRatio.toFixed(1)}%`,
          status: liquidityRatio < 5 ? 'warning' : liquidityRatio > 20 ? 'warning' : 'good',
          icon: liquidityRatio < 5 || liquidityRatio > 20 ? ArrowUp : ArrowDown
        },
        {
          label: 'Performance',
          value: formatPercent(plPercent),
          status: plPercent < 0 ? 'bad' : plPercent > 5 ? 'good' : 'neutral',
          icon: plPercent >= 0 ? TrendingUp : TrendingDown
        },
        {
          label: 'Risk Level',
          value: data.volatility ? `${data.volatility.toFixed(1)}%` : 'N/A',
          status: (data.volatility || 0) > 15 ? 'warning' : 'good',
          icon: (data.volatility || 0) > 15 ? ArrowUp : ArrowDown
        }
      ];
    }

    return [];
  };

  const keyDrivers = getKeyDrivers();

  if (loading) {
    return (
      <section className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-3">
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center gap-6">
        {/* Health Score Gauge - conditionally rendered */}
        {!hideHealthScore && <HealthScoreGauge score={healthScore} />}

        {/* Metrics and Key Drivers */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {mode === 'global' ? 'All Portfolios' : 'Portfolio'} Health Score
              </h3>
              {/* Using reusable PortfolioValue component */}
              <div className="mt-2">
                <PortfolioValue
                  value={mode === 'global' 
                    ? (globalSummary?.totalAUM || 0)
                    : (data?.total_value || 0)}
                  change={mode === 'client' ? dailyChange : undefined}
                  label=""
                  size="sm"
                  showChange={mode === 'client'}
                  className="shadow-none border-0 p-0"
                />
              </div>
            </div>
            
            {/* Traffic Light Status */}
            <div className="text-center">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-5 h-5 rounded-full ${
                  trafficLight.color === 'bg-green-500' ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <div className={`w-5 h-5 rounded-full ${
                  trafficLight.color === 'bg-yellow-500' ? 'bg-yellow-500' : 'bg-gray-300'
                }`}></div>
                <div className={`w-5 h-5 rounded-full ${
                  trafficLight.color === 'bg-red-500' ? 'bg-red-500' : 'bg-gray-300'
                }`}></div>
              </div>
              <span className="text-sm font-medium text-gray-700">{trafficLight.label}</span>
            </div>
          </div>
          
          {/* Key Drivers */}
          <div className="border-t pt-4">
            <div className="font-semibold text-gray-900 mb-3">Key Drivers:</div>
            <div className="space-y-2">
              {keyDrivers.map((driver, index) => {
                const Icon = driver.icon;
                const colorClass = driver.status === 'good' ? 'text-green-500' :
                                  driver.status === 'warning' ? 'text-yellow-500' :
                                  driver.status === 'bad' ? 'text-red-500' : 'text-gray-500';
                
                return (
                  <div key={index} className="flex items-center">
                    <Icon className={`w-4 h-4 ${colorClass} mr-2`} />
                    <span className="text-sm text-gray-600">{driver.label}:</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">{driver.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Additional Metrics using MetricCard components */}
          {mode === 'client' && data && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <MetricCard
                label="Unrealized P&L"
                value={formatCurrency(data.total_unrealized_pl)}
                status={data.total_unrealized_pl >= 0 ? 'good' : 'critical'}
                size="sm"
                className="bg-transparent border-0 p-0"
              />
              {data.sharpe_ratio != null && (
                <MetricCard
                  label="Sharpe Ratio"
                  value={data.sharpe_ratio.toFixed(2)}
                  status="neutral"
                  size="sm"
                  className="bg-transparent border-0 p-0"
                />
              )}
              {data.var_1day != null && (
                <MetricCard
                  label="1-Day VaR (95%)"
                  value={formatCurrency(data.var_1day)}
                  status="neutral"
                  size="sm"
                  className="bg-transparent border-0 p-0"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ExecutiveSummary;